import { useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { InputProps } from "../../../types";
import roundSlider from './round-slider.js';

type SliderStateType = "empty" | "initialized" | "changing" | "destroying";

export const useSliderRef = (args: InputProps<number>) => {
  const slider = useRef<HTMLDivElement>(null);
  const [screenWidth, setScreenWidth] = useState<number>(0);
  const [sliderState, setSliderState] = useState<SliderStateType>("empty");
  const [sliderValue, setSliderValue] = useState<number>();
  const [loading, setLoading] = useState(true);
  const getSliderAmountValue = useCallback(() => {
    return slider.current ? parseInt(slider.current.querySelector(".slider-amount")?.textContent?.replace(",", "") || '') : 0;
  }, [slider.current]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => setScreenWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);
      handleResize();
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  useLayoutEffect(() => {
    switch (sliderState) {
      case "empty":
        if (slider?.current && screenWidth)
          initializeSlider(slider.current, screenWidth, args.onChange, args.defaultValue, sliderValue);
        setSliderValue(undefined);
        setSliderState("initialized");
        break;
      case "changing":
        if (slider?.current) {
          setSliderValue(getSliderAmountValue());
        }
        break;
      case "destroying":
        if (slider?.current) {
          destroySlider(slider.current);
          setSliderState("empty");
        }
        break;
      case "initialized":
        setLoading(false);
        break;
      default:
        break;
    }
  }, [sliderState]);

  useEffect(() => {
    if (sliderState === "initialized") setSliderState("changing");
  }, [screenWidth]);

  useEffect(() => {
    if (sliderState === "changing") setSliderState("destroying");
  }, [sliderValue]);

  return {
    slider,
    loading,
    add: () => {
      const sliderValue = getSliderAmountValue();
      const addValue = sliderValue + 100 > 1500 ? 1500 : sliderValue + 100;
      const value = 1600 - addValue;
      roundSlider(slider.current, "setValue", value);
    },
    subtract: () => {
      const sliderValue = getSliderAmountValue();
      const subValue = sliderValue - 100 < 100 ? 100 : sliderValue - 100;
      const value = 1600 - subValue;
      roundSlider(slider.current, "setValue", value);
    }
  };
};

const getSpacingSpecs = ( screenWidth: number ): { componentWidth: number; tooltipTrackLength: number; initialTooltipPosition: number } => {
  if (screenWidth > 1024)
    return {
      componentWidth: 462,
      tooltipTrackLength: 462 - 270,
      initialTooltipPosition: 70,
    };

  if (screenWidth > 768)
    return {
      componentWidth: 353,
      tooltipTrackLength: 353 - 261,
      initialTooltipPosition: 60,
    };

  return {
    componentWidth: 281,
    tooltipTrackLength: 281 - 252,
    initialTooltipPosition: 50,
  };
};

const getSliderRadius = (screenWidth: number) => {
  const { componentWidth } = getSpacingSpecs(screenWidth);
  return (componentWidth * Math.sqrt(2) - 100) / 2;
};

const getTooltipPosition = (value: number, screenWidth: number) => {
  const { tooltipTrackLength, initialTooltipPosition } = getSpacingSpecs(screenWidth);

  if (value <= 450) return initialTooltipPosition;
  if (value >= 1150) return tooltipTrackLength + initialTooltipPosition;

  return initialTooltipPosition + ((value - 450) / 700) * tooltipTrackLength;
};

const formatValue = (value: number) => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const initializeSlider = (
  elem: HTMLDivElement,
  screenWidth: number,
  onValueChange?: (v: number) => void,
  defaultValue?: number,
  value?: number
) => {
  const initialValue = value ? 1600 - value : defaultValue ? 1600 - defaultValue : 1100;
  
  roundSlider(elem, {
    animation: false,
    sliderType: "min-range",
    circleShape: "custom-quarter",
    min: 100,
    max: 1500,
    value: initialValue,
    startAngle: 225,
    editableTooltip: false,
    radius: getSliderRadius(screenWidth),
    width: 3,
    step: 10,
    handleSize: "+47",
    showTooltip: false,
    change: (e: {value: number}) => (onValueChange?.(1600 - e.value)),
    tooltipFormat: (e: {value: number}) => {
      const val = 1600 - e.value;
      onValueChange?.(val);
      
      let sliderAmount = elem.querySelector(".slider-amount")
      if (sliderAmount) sliderAmount.textContent = formatValue(val);
      let sliderTooltip = elem.querySelector(".slider-tooltip")
      if (sliderTooltip) (sliderTooltip as HTMLElement).style.left = `${getTooltipPosition(val, screenWidth)}px`;

      return `$${val}`;
    }
  });

  // initialize tooltip
  let tooltip = document.createElement('div')
  tooltip.classList.add('slider-tooltip')
  tooltip.style.left = getTooltipPosition( 1600 - initialValue, screenWidth) + 'px'
  tooltip.innerHTML = `<span class="slider-currency">$</span><p class="slider-amount">${1600 - initialValue}</p>`
  if (elem) elem.insertBefore(tooltip, elem.firstChild);
};

export const destroySlider = (elem: HTMLDivElement) => {
  roundSlider(elem, "destroy");
};